import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { usePlatform } from '../hooks/usePlatform';

interface MobilePageHeaderProps {
  title: string;
  onBack?: () => void;
  right?: React.ReactNode;
}

/**
 * Full-width sticky header for detail/form pages on mobile.
 * - Glassy liquid back button with rounded border
 * - Centred title
 * - Optional right action slot
 * Desktop: hidden
 */
export function MobilePageHeader({ title, onBack, right }: MobilePageHeaderProps) {
  const navigate = useNavigate();
  const { isMobile } = usePlatform();

  if (!isMobile) return null;

  const handleBack = onBack ?? (() => navigate(-1));

  return (
    <div className="sticky top-0 z-30 -mx-3 px-3 h-13 flex items-center justify-between mb-2 bg-background/70 backdrop-blur-xl backdrop-saturate-150">
      {/* Back button — glassy pill with border */}
      <button
        onClick={handleBack}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-border/50 bg-white/10 dark:bg-white/5 backdrop-blur-sm shadow-sm active:scale-95 transition-transform min-w-[60px]"
        aria-label="Go back"
      >
        <ChevronLeft className="h-4 w-4 text-foreground" />
        <span className="text-sm text-foreground">Back</span>
      </button>

      {/* Centred title */}
      <h1 className="absolute left-1/2 -translate-x-1/2 text-sm font-semibold text-foreground truncate max-w-[55%] text-center">
        {title}
      </h1>

      {/* Right slot */}
      <div className="min-w-[60px] flex justify-end">
        {right ?? null}
      </div>
    </div>
  );
}
